import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../services/api.service';
import { ReportType, Report, CreateReportRequest, ReportListResponse } from '../../models/report.model';

@Component({
  selector: 'app-reports',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss']
})
export class ReportsComponent implements OnInit {
  // Form data
  reportForm: CreateReportRequest = {
    typeName: '',
    title: '',
    module: '',
    symptoms: '',
    solution: '',
    additionalNotes: ''
  };

  // Autocomplete
  reportTypes: ReportType[] = [];
  filteredTypes: ReportType[] = [];
  showTypeDropdown: boolean = false;

  // Reports table
  reports: Report[] = [];
  totalCount: number = 0;
  currentPage: number = 1;
  pageSize: number = 20;

  // Filters
  typeFilter: string = '';
  moduleFilter: string = '';
  availableModules: string[] = [];

  // State
  isLoading: boolean = false;
  isSubmitting: boolean = false;
  error: string | null = null;
  successMessage: string | null = null;

  constructor(private apiService: ApiService) {}

  ngOnInit(): void {
    this.loadReportTypes();
    this.loadReports();
    this.loadModules();
  }

  loadReportTypes(): void {
    this.apiService.getReportTypes().subscribe({
      next: (types) => {
        this.reportTypes = types;
        this.filteredTypes = types;
      },
      error: (err) => {
        console.error('Failed to load report types', err);
      }
    });
  }

  loadReports(): void {
    this.isLoading = true;
    this.apiService.getReports(this.currentPage, this.pageSize, this.typeFilter || undefined, this.moduleFilter || undefined)
      .subscribe({
        next: (response: ReportListResponse) => {
          this.reports = response.reports;
          this.totalCount = response.totalCount;
          this.isLoading = false;
        },
        error: (err) => {
          this.error = 'Failed to load reports: ' + (err.error?.message || err.message);
          this.isLoading = false;
        }
      });
  }

  loadModules(): void {
    this.apiService.getReportModules().subscribe({
      next: (modules) => {
        this.availableModules = modules;
      },
      error: (err) => {
        console.error('Failed to load modules', err);
      }
    });
  }

  onTypeInput(): void {
    const input = this.reportForm.typeName.toLowerCase();
    this.filteredTypes = this.reportTypes.filter(t =>
      t.typeName.toLowerCase().includes(input)
    );
    this.showTypeDropdown = true;
  }

  selectType(type: ReportType): void {
    this.reportForm.typeName = type.typeName;
    this.showTypeDropdown = false;
  }

  hideTypeDropdown(): void {
    setTimeout(() => this.showTypeDropdown = false, 200);
  }

  submitReport(): void {
    if (!this.reportForm.typeName.trim() || !this.reportForm.title.trim()) {
      this.error = 'Type and Title are required';
      return;
    }

    this.isSubmitting = true;
    this.error = null;

    this.apiService.createReport(this.reportForm).subscribe({
      next: (report) => {
        this.successMessage = `Report "${report.title}" created successfully` +
          (report.uploadedToRag ? ' and uploaded to RAG' : '');
        this.resetForm();
        this.loadReports();
        this.loadReportTypes();
        this.loadModules();
        this.isSubmitting = false;
        setTimeout(() => this.successMessage = null, 5000);
      },
      error: (err) => {
        this.error = 'Failed to create report: ' + (err.error || err.message);
        this.isSubmitting = false;
      }
    });
  }

  resetForm(): void {
    this.reportForm = {
      typeName: '',
      title: '',
      module: '',
      symptoms: '',
      solution: '',
      additionalNotes: ''
    };
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.loadReports();
  }

  clearFilters(): void {
    this.typeFilter = '';
    this.moduleFilter = '';
    this.currentPage = 1;
    this.loadReports();
  }

  get totalPages(): number {
    return Math.ceil(this.totalCount / this.pageSize);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadReports();
    }
  }

  retryingReportId: number | null = null;

  retryUpload(report: Report): void {
    if (report.uploadedToRag) return;

    this.retryingReportId = report.reportID;
    this.error = null;

    this.apiService.retryReportUpload(report.reportID).subscribe({
      next: (updatedReport) => {
        this.successMessage = `Report "${updatedReport.title}" uploaded to RAG successfully`;
        this.retryingReportId = null;
        this.loadReports();
        setTimeout(() => this.successMessage = null, 5000);
      },
      error: (err) => {
        this.error = 'Failed to upload: ' + (err.error || err.message);
        this.retryingReportId = null;
      }
    });
  }
}

